import { styled } from '@/styles/stitches.config';

type TermsOfUseFrProps = {
  title: string;
};

const Section = styled('section', {
  marginBlock: '$8',
});

const H1 = styled('h1', {
  color: '$white',
  marginBlockEnd: '$8',
});

const H2 = styled('h2', {
  color: '$white',
  fontWeight: '$normal',
  fontSize: '$3',
});

const StyledA = styled('a', {
  color: '$white',
});

type AProps = {
  href: string;
  children: React.ReactNode;
};

const A = (props: AProps) => {
  return (
    <StyledA href={props.href} rel="noopener noreferrer" target={'_blank'}>
      {props.children}
    </StyledA>
  );
};

export function TermsOfUseFr(props: TermsOfUseFrProps) {
  const { title } = props;

  return (
    <>
      <H1>{title}</H1>

      <Section>
        <H2>1. Accord avec l’utilisateur</H2>
        <p>
          Le site Internet du projet <i>L’enfance en évolution</i> héberge des ressources en lien
          avec le mandat du Fonds des Nations Unies pour l’enfance (UNICEF). Il présente notamment
          les résultats d’un sondage mondial réalisé par l’UNICEF et ses partenaires sur le thème de
          l’enfance au XXIe siècle. Les ressources mises à disposition, qui comprennent, entre
          autres, des statistiques, des infographies, des animations, des vidéos, des articles et
          des contenus éditoriaux, ont pour objectif d’élargir les connaissances du public et sa
          compréhension de certains sujets. Le contenu du site est uniquement réservé à votre usage
          personnel et ne peut être utilisé à des fins commerciales.
        </p>
        <p>
          Veuillez lire attentivement les présentes Conditions d’utilisation. Elles constituent un
          accord entre l’UNICEF et vous. En poursuivant votre navigation sur ce site, vous
          reconnaissez avoir lu, compris et accepté les conditions figurant ci-après. Si vous êtes
          en désaccord avec l’une ou plusieurs d’entre elles, veuillez ne pas poursuivre votre
          navigation.
        </p>
        <p>
          Les présentes Conditions d’utilisation étant susceptibles de changer sans préavis,
          assurez-vous de consulter régulièrement cette page pour prendre connaissance des
          éventuelles mises à jour. La date de la dernière mise à jour est indiquée au bas de cette
          page. La poursuite de votre navigation sur ce site vaut pour acceptation de toute
          modification ou révision apportée aux présentes Conditions d’utilisation.
        </p>
        <p>
          Dans le cadre des présentes Conditions d’utilisation, “nous,” “notre,” and “nos” désignent
          l’UNICEF. Les termes “vous,” “votre,” “vos” and “utilisateur” désignent la personne ou
          l’organisation qui se connecte au site ou le consulte.
        </p>
      </Section>

      <Section>
        <H2>2. Contenu du site et règles d’usage</H2>
        <p>
          Le site <i>Transformation de l’enfance</i> est une communauté ouverte aux internautes du
          monde entier. Par son biais, l’UNICEF cherche à créer un cadre dans lequel chacun se
          sentira à l’aise, indépendamment de sa nationalité, de son genre, de son appartenance
          culturelle, de sa religion, de son orientation sexuelle ou de ses convictions politiques.
        </p>
        <p>
          Vous vous engagez à n’utiliser ce site qu’à des fins licites, et d’une manière qui ne
          porte pas atteinte aux droits d’un tiers, ou ne restreigne ni ne limite son utilisation et
          sa jouissance de ce site.
        </p>
        <p>
          L’UNICEF possède et conserve tous les droits sur le code source du site, son design, ses
          fonctionnalités et son architecture, et sur tout logiciel, outil ou contenu y figurant
          (collectivement la{' '}
          <i>propriété intellectuelle du site Internet du projet "Transformation de l’enfance"</i>).
          À l’exception de tous droits explicitement accordés par les présentes, l’utilisateur ne
          détient aucun droit sur la propriété intellectuelle du site Internet du projet{' '}
          <i>Transformation de l’enfance</i>. En cas d’usage non autorisé par les présentes
          Conditions d’utilisation, veuillez contacter au préalable l’équipe du site afin d’obtenir
          son autorisation écrite.
        </p>
        <p>
          Sauf indication contraire, toutes les ressources fournies par l’UNICEF sur ce site sont
          sous licence Creative Commons Attribution - Pas d’Utilisation Commerciale - Pas de
          Modification 4.0 Non transposée. Si cette licence autorise les utilisateurs à copier et à
          redistribuer le contenu figurant sur ce site, elle n’autorise cependant pas son
          adaptation, sa transformation ou encore son utilisation à des fins commerciales. Cette
          licence prévoit que vous créditiez le titulaire originaire des droits à chaque fois que
          vous utilisez un de ses supports. Si vous utilisez{' '}
          <strong>une ou plusieurs ressources du site</strong>, merci d’utiliser le crédit: “Enquête
          menée par Gallup pour le compte de l’UNICEF. Pour plus d’informations, veuillez consulter
          le site changingchildhood.unicef.org". Pour toute autre question et pour plus
          d’informations, n’hésitez pas à consulter la{' '}
          <A href="https://creativecommons.org/faq/">FAQ du site Creative Commons</A>.
        </p>
        <p>
          Il convient de noter que des images, sons et vidéos sous marque déposée par des tiers
          peuvent figurer sur ce site et dans ses ressources. Aucune disposition des présentes
          Conditions d’utilisation ou de la licence Creative Commons ne limite ni ne réduit les
          pleins droits de propriété intellectuelle de tiers. Ces supports ne pourront être utilisés
          pour étiqueter, promouvoir ou recommander un quelconque produit ou service. L’utilisateur
          de ce site est entièrement responsable de toute atteinte aux droits de propriété
          intellectuelle d’un tiers causée par la mauvaise utilisation de ces supports.
        </p>
        <p>
          L’utilisateur s’engage à respecter l’ensemble des lois et réglementations applicables dans
          son pays lorsqu’il utilise ce site.
        </p>
        <p>
          Les noms et logos du site Internet du projet <i>Transformation de l’enfance</i> et de
          l’UNICEF relèvent de la propriété exclusive de l’UNICEF. Ils sont protégés par la
          législation nationale et internationale. Toute utilisation non autorisée de ceux-ci est
          interdite. Ils ne peuvent donc être copiés ou reproduits d’une quelconque manière sans
          l’autorisation écrite préalable de l’UNICEF. Les demandes d’autorisation doivent être
          adressées à <A href="mailto:nyhqdoc.permit@unicef.org">nyhqdoc.permit@unicef.org</A>. Tous
          les autres logos, marques déposées et marques de service utilisés sur ce site, qu’ils
          soient crédités ou non, appartiennent à leurs propriétaires respectifs.
        </p>
        <p>
          L’UNICEF se réserve le droit d’invoquer toute voie de recours prévue par la loi en cas de
          violation de ces Conditions d’utilisation, y compris toute violation du droit de l’UNICEF
          à son nom et à son emblème ou des droits ayant trait aux informations, documents, texte,
          matériel vidéo ou audio, photographies ou tout autre contenu se trouvant sur ce site.
        </p>
      </Section>

      <Section>
        <H2>3. Privacy, Cookies and Information Disclosure</H2>
        <p>
          Nous accordons une grande importance à votre vie privée. Nous vous invitons à lire notre{' '}
          <A href="https://www.unicef.org/fr/informations-legales#politique-de-confidentialite">
            Politique de confidentialité
          </A>
          , qui explique le type d’informations collectées, stockées et utilisées par l’UNICEF et
          les modalités de traitement de ces informations. En poursuivant votre navigation, vous
          reconnaissez avoir lu et accepté cette{' '}
          <A href="https://www.unicef.org/fr/informations-legales#politique-de-confidentialite">
            politique
          </A>
          .
        </p>
        <p>
          L’UNICEF utilise des cookies sur ce site pour analyser l’usage qui en est fait. Nous vous
          invitons à vous référer à la{' '}
          <A href="https://www.unicef.org/fr/informations-legales#politique-de-cookies">
            Politique de l’UNICEF en matière de cookies
          </A>
          pour plus d’informations.
        </p>
        <p>
          L’UNICEF s’engage à rendre publique l’information sur ses programmes et opérations.
          Veuillez consulter la{' '}
          <A href="https://www.unicef.org/fr/informations-legales#divulgation-information">
            Politique de l’UNICEF en matière de divulgation de l’information
          </A>{' '}
          pour plus de détails.
        </p>
      </Section>

      <Section>
        <H2>4. Liens vers d’autres sites</H2>
        <p>
          Ce site inclut des liens vers d’autres sites Internet sur lesquels l’UNICEF n’exerce aucun
          contrôle. L’UNICEF fournit ces liens uniquement pour la commodité des utilisateurs et
          l’adjonction de ces sites ne constitue ni une promotion ni une approbation par l’UNICEF de
          quelque site Internet, produit ou service que ce soit. La décision d’accéder à des
          applications ou des sites tiers à partir de ces liens relève de la seule responsabilité de
          l’utilisateur. L’UNICEF décline toute responsabilité concernant ces sites Internet, y
          compris par exemple une quelconque responsabilité concernant la véracité ou la fiabilité
          des informations, données, opinions, conseils ou déclarations publiés sur ceux-ci.
        </p>
      </Section>

      <Section>
        <H2>5. Clause de non-responsabilité</H2>
        <p>
          Tous les contenus fournis par l’UNICEF sur ce site sont mis à disposition en l’état. Nous
          ne pouvons donc garantir que toutes les informations que vous trouverez sur ce site seront
          parfaitement exactes ou dénuées d’erreur.
        </p>
        <p>
          Les Nations Unies et l’UNICEF déclinent toute responsabilité résultant de l’utilisation de
          ce site ou de son contenu. Les Nations Unies, et notamment l’UNICEF, ainsi que leurs
          responsables, membres du personnel, consultants, agents et sous-traitants, ne pourront en
          aucun cas être tenus pour responsables des conséquences financières ou autres résultant de
          l’utilisation du contenu de ce site, y compris l’usage abusif, incorrect ou frauduleux
          dudit contenu.
        </p>
        <p>
          L’UNICEF décline toute responsabilité à l’égard de la conduite des utilisateurs en
          relation avec ce site ou à l’égard de toute ressource mise en ligne par les utilisateurs
          sur ce site, y compris, par exemple, toute responsabilité concernant la fiabilité ou la
          véracité des informations, données, opinions, conseils ou déclarations apparaissant dans
          lesdites ressources.
        </p>
        <p>
          L’UNICEF ne peut garantir que les fonctionnalités présentes dans le contenu ou les
          ressources de ce site seront accessibles sans interruption et ne comporteront pas
          d’erreur, que les défauts en seront corrigés, ou que ce site ou son serveur ne comportent
          pas de virus et offrent le meilleur degré possible de fonctionnalité, d’exactitude et de
          fiabilité. L’UNICEF ne fait aucune déclaration ni ne donne aucune garantie concernant ce
          site, et rejette toute responsabilité en cas d’infection par un virus informatique ou par
          quelque contamination ou procédé de nature destructive que ce soit.
        </p>
        <p>
          Vous vous engagez à indemniser, à vos frais, l’UNICEF, ses responsables, membres du
          personnel, consultants et agents, contre toute réclamation de tiers, et notamment à
          couvrir leurs coûts et frais, en lien avec ou découlant de (a) votre utilisation ou
          tentative d’utilisation de ce site, de ses ressources ou services en violation des
          présentes Conditions d’utilisation; (b) votre violation de toute disposition législative
          ou droit de tiers; et (c) tout contenu posté ou mis à disposition d’une quelconque manière
          par vos soins sur ce site ou via tout service – et notamment, entre autres, contre toute
          réclamation relative à une violation ou à un détournement des droits de propriété
          intellectuelle ou de tout autre droit de propriété.
        </p>
        <p>
          Sauf disposition contraire expresse figurant dans les présentes Conditions d’utilisation,
          l’UNICEF ne formule aucune déclaration ni garantie, expresse ou implicite, concernant les
          services. Par exemple, nous ne prenons aucun engagement au sujet du contenu mis à
          disposition, des fonctionnalités propres aux services, ou au sujet de leur fiabilité, de
          leur disponibilité ou de leur adéquation à vos besoins. En vertu de tout ce qui précède,
          l’UNICEF décline toute garantie implicite de qualité marchande, d’adéquation à l’usage
          prévu et de non-contrefaçon.
        </p>
        <p>
          L’UNICEF ajoute régulièrement du contenu sur le site et modifie, améliore ou met
          régulièrement à jour le contenu existant sans avis préalable. L’UNICEF ne saurait en aucun
          cas être tenu pour responsable d’un quelconque préjudice ou d’une quelconque perte,
          obligation ou dépense imputés à l’utilisation de ce site, et notamment à une défaillance,
          erreur, omission, interruption ou à un retard quelconque, entre autres. L’usage qui est
          fait de ce site relève de la seule responsabilité de l’utilisateur. L’UNICEF ou ses
          organismes affiliés ne sauraient en aucun cas, y compris en cas de négligence, être tenus
          pour responsables d’un quelconque préjudice, quand bien même l’UNICEF aurait été avisé
          d’une telle éventualité. Veillez à utiliser le contenu de ce site avec discernement et de
          manière responsable.
        </p>
        <p>
          Vous reconnaissez être entièrement responsable de tout problème rencontré dans
          l’utilisation de ce site. L’UNICEF ne peut en aucun cas être tenu pour responsable de tout
          préjudice ou blessure découlant de l’utilisation de ce site.
        </p>
        <p>
          La mention du nom de telle compagnie ou tel produit n’implique en rien une intention de
          porter atteinte à des droits de propriété, pas plus qu’elle ne peut être interprétée comme
          une promotion ou une recommandation de la part de l’UNICEF.
        </p>
        <p>
          L’utilisation d’un nom précis pour désigner tel pays ou territoire ne reflète aucunement
          une position de l’UNICEF sur le statut légal de ce pays ou territoire, ni sur ses
          institutions ou les autorités qui le dirigent, ni sur la délimitation de ses frontières.
        </p>
      </Section>

      <Section>
        <H2>6. Privilèges et immunités</H2>
        <p>
          Aucune des dispositions des présentes Conditions d’utilisations ou relatives aux présentes
          ne saurait être considérée d’une quelconque manière comme une dérogation, expresse ou
          implicite, aux privilèges et immunités de l’UNICEF, conformément à la Convention sur les
          Privilèges et Immunités des Nations Unies de 1946.
        </p>
      </Section>

      <Section>
        <H2>7. Nous contacter</H2>
        <p>
          Pour toute question relative à ce site, veuillez utiliser ce{' '}
          <A href="https://www.unicef.org/about-unicef/contact-us">formulaire de contact</A>.
        </p>
      </Section>

      <footer>
        <p>Dernière mise à jour: 12 juillet 2021</p>
      </footer>
    </>
  );
}
